import React, { useState } from 'react';
import { useTranslation } from '../Context/TranslationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const ProgramsPage = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const translations = {
    pageTitle: {
      en: "Scouting Programs",
      ta: "சாரணர் திட்டங்கள்"
    },
    pageDescription: {
      en: "The Scouting experience is divided into three programs according to age and activities.",
      ta: "சாரணர் அனுபவம் வயது மற்றும் செயல்பாடுகளின் அடிப்படையில் மூன்று திட்டங்களாக பிரிக்கப்பட்டுள்ளது."
    },
    programs: {
      bunny: {
        title: { en: "Bunny", ta: "பன்னி" },
        age: { en: "Age: 3-6 years", ta: "வயது: 3-6 வருடங்கள்" },
        description: { 
          en: "The Bunny program is designed for children aged 3 to 6, fostering early development and social skills. The program includes creative activities, nature exploration, and group play. Trained female leaders (Bunny Aunties) ensure a safe and nurturing environment.\n\nBunny Law: I shall try to be a good boy/girl\nMotto: Keep Smiling\nSalute: Two fingers raised alongside ears\nUniform: Bright apron with Bunny symbol",
          ta: "பன்னி திட்டம் 3 முதல் 6 வயது வரையிலான குழந்தைகளுக்காக வடிவமைக்கப்பட்டுள்ளது. இந்த திட்டம் படைப்பாற்றல் செயல்பாடுகள், இயற்கை ஆராய்ச்சி மற்றும் குழு விளையாட்டுகளை உள்ளடக்கியது. பயிற்சி பெற்ற பெண் தலைவர்கள் (பன்னி ஆண்டீஸ்) பாதுகாப்பான சூழலை உறுதி செய்கின்றனர்.\n\nபன்னி சட்டம்: நான் நல்ல சிறுவன்/சிறுமியாக இருக்க முயற்சிப்பேன்\nகுறிக்கோள்: புன்னகையுடன் இருங்கள்\nவணக்கம்: இரண்டு விரல்கள் காதுகளுக்கு அருகில் உயர்த்தப்பட்டிருக்கும்\nசீருடை: பன்னி சின்னத்துடன் கூடிய பிரகாசமான முன்தாணை"
        }
      },
      cubs: {
        title: { en: "Cubs", ta: "கப்ஸ்" },
        age: { en: "Age: 5-10 years", ta: "வயது: 5-10 வருடங்கள்" },
        description: { 
          en: "Cubs program focuses on character building through outdoor activities and team projects. Boys learn basic scouting skills, nature craft, and community service.\n\nPravesh Requirements:\n- Jungle story knowledge\n- Understanding of Cub Law and Promise\n- Basic scouting skills\n\nMotto: Do Your Best\nUniform: Standard Cub uniform with badges",
          ta: "கப்ஸ் திட்டம் வெளிப்புற செயல்பாடுகள் மற்றும் குழு திட்டங்கள் மூலம் குணாதிசய வளர்ச்சியில் கவனம் செலுத்துகிறது. சிறுவர்கள் அடிப்படை சாரணர் திறன்கள், இயற்கை கைவினை மற்றும் சமூக சேவையை கற்றுக்கொள்கிறார்கள்.\n\nபிரவேஷ் தேவைகள்:\n- காட்டு கதை அறிவு\n- கப் சட்டம் மற்றும் வாக்குறுதி புரிதல்\n- அடிப்படை சாரணர் திறன்கள்\n\nகுறிக்கோள்: சிறந்ததை செய்யுங்கள்\nசீருடை: பேட்ஜ்களுடன் கூடிய நிலையான கப் சீருடை"
        }
      },
      bulbuls: {
        title: { en: "BulBuls", ta: "புல்புல்ஸ்" },
        age: { en: "Age: 5-10 years", ta: "வயது: 5-10 வருடங்கள்" },
        description: { 
          en: "BulBuls program helps young girls develop leadership skills and self-confidence. Activities include nature study, crafts, and community service.\n\nPravesh Requirements:\n- Understanding of BulBul Law\n- Basic scouting skills\n- Community awareness\n\nMotto: Do Your Best\nUniform: BulBul uniform with relevant badges",
          ta: "புல்புல்ஸ் திட்டம் இளம் பெண்கள் தலைமைத்துவ திறன்கள் மற்றும் சுய நம்பிக்கையை வளர்க்க உதவுகிறது. இயற்கை ஆய்வு, கைவினை மற்றும் சமூக சேவை ஆகியவை இதில் அடங்கும்.\n\nபிரவேஷ் தேவைகள்:\n- புல்புல் சட்டம் புரிதல்\n- அடிப்படை சாரணர் திறன்கள்\n- சமூக விழிப்புணர்வு\n\nகுறிக்கோள்: சிறந்ததை செய்யுங்கள்\nசீருடை: தொடர்புடைய பேட்ஜ்களுடன் புல்புல் சீருடை"
        }
      },
      scouts: {
        title: { en: "Scouts", ta: "சாரணர்கள்" },
        age: { en: "Age: 10-17 years", ta: "வயது: 10-17 வருடங்கள்" },
        description: { 
          en: "Scouts program offers advanced outdoor skills training and leadership development. Focus on community service and personal growth.\n\nRequirements:\n- Advanced scouting skills\n- Leadership training\n- Community service projects\n\nMotto: Be Prepared\nUniform: Scout uniform with earned badges",
          ta: "சாரணர் திட்டம் மேம்பட்ட வெளிப்புற திறன் பயிற்சி மற்றும் தலைமைத்துவ மேம்பாட்டை வழங்குகிறது. சமூக சேவை மற்றும் தனிப்பட்ட வளர்ச்சியில் கவனம்.\n\nதேவைகள்:\n- மேம்பட்ட சாரணர் திறன்கள்\n- தலைமைத்துவ பயிற்சி\n- சமூக சேவை திட்டங்கள்\n\nகுறிக்கோள்: தயாராக இருங்கள்\nசீருடை: பெறப்பட்ட பேட்ஜ்களுடன் சாரணர் சீருடை"
        }
      },
      guide: {
        title: { en: "Guide", ta: "கைடு" },
        age: { en: "Age: 10-17 years", ta: "வயது: 10-17 வருடங்கள்" },
        description: { 
          en: "Guide program empowers young women through leadership training and life skills development.\n\nKey Activities:\n- Leadership development\n- Community service\n- Outdoor skills\n\nMotto: Be Prepared\nUniform: Guide uniform with earned badges",
          ta: "கைடு திட்டம் தலைமைத்துவ பயிற்சி மற்றும் வாழ்க்கைத் திறன் மேம்பாடு மூலம் இளம் பெண்களுக்கு அதிகாரமளிக்கிறது.\n\nமுக்கிய செயல்பாடுகள்:\n- தலைமைத்துவ மேம்பாடு\n- சமூக சேவை\n- வெளிப்புற திறன்கள்\n\nகுறிக்கோள்: தயாராக இருங்கள்\nசீருடை: பெறப்பட்ட பேட்ஜ்களுடன் கைடு சீருடை"
        }
      },
      rover: {
        title: { en: "Rover", ta: "ரோவர்" },
        age: { en: "Age: 15-25 years", ta: "வயது: 15-25 வருடங்கள்" },
        description: { 
          en: "Advanced leadership and service program for young men. Includes specialized training in various domains.\n\nProgram Stages:\n- Rover Aspirant\n- Rover Scout\n- Rover Leader\n\nFocus on community development and specialized skills training.",
          ta: "இளைஞர்களுக்கான மேம்பட்ட தலைமைத்துவ மற்றும் சேவை திட்டம். பல்வேறு துறைகளில் சிறப்பு பயிற்சி அடங்கும்.\n\nதிட்ட நிலைகள்:\n- ரோவர் ஆர்வலர்\n- ரோவர் சாரணர்\n- ரோவர் தலைவர்\n\nசமூக மேம்பாடு மற்றும் சிறப்பு திறன் பயிற்சியில் கவனம்."
        }
      },
      ranger: {
        title: { en: "Ranger", ta: "ரேஞ்சர்" },
        age: { en: "Age: 15-25 years", ta: "வயது: 15-25 வருடங்கள்" },
        description: { 
          en: "Advanced program for young women focusing on leadership and specialized skills.\n\nProgram Levels:\n- Ranger Aspirant\n- Ranger Guide\n- Ranger Leader\n\nEmphasis on personal development and community service.",
          ta: "தலைமைத்துவம் மற்றும் சிறப்பு திறன்களில் கவனம் செலுத்தும் இளம் பெண்களுக்கான மேம்பட்ட திட்டம்.\n\nதிட்ட நிலைகள்:\n- ரேஞ்சர் ஆர்வலர்\n- ரேஞ்சர் கைடு\n- ரேஞ்சர் தலைவர்\n\nதனிப்பட்ட மேம்பாடு மற்றும் சமூக சேவையில் முக்கியத்துவம்."
        }
      }
    }
  };

  const programSections = [
    'bunny',
    'cubs',
    'bulbuls',
    'scouts',
    'guide',
    'rover',
    'ranger'
  ];

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF8] mt-36 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className={`text-[#2C251D] mb-4 ${
            isTamil 
              ? 'text-3xl sm:text-4xl' 
              : 'text-4xl sm:text-5xl'
          } font-bold`}>
            {translations.pageTitle[isTamil ? 'ta' : 'en']}
          </h1>
          <p className={`text-[#645D57] ${
            isTamil ? 'text-lg' : 'text-xl'
          }`}>
            {translations.pageDescription[isTamil ? 'ta' : 'en']}
          </p>
        </div>

        {/* Programs List */}
        <div className="space-y-4">
          {programSections.map((program) => (
            <div 
              key={program}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleSection(program)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  <img 
                    src={`/Images/${program}.png`}
                    alt=""
                    className="w-6 h-6"
                  />
                  <span className={`text-[#2C251D] font-medium ${
                    isTamil ? 'text-lg' : 'text-xl'
                  }`}>
                    {translations.programs[program].title[isTamil ? 'ta' : 'en']}
                  </span>
                </div>
                <ChevronDown 
                  className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                    expandedSection === program ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <AnimatePresence>
                {expandedSection === program && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-6 py-4 border-t border-gray-100">
                      <div className="space-y-4">
                        <p className={`text-[#2C251D] font-medium ${
                          isTamil ? 'text-base' : 'text-lg'
                        }`}>
                          {translations.programs[program].age[isTamil ? 'ta' : 'en']}
                        </p>
                        <p className={`text-[#645D57] whitespace-pre-line ${
                          isTamil ? 'text-base' : 'text-lg'
                        }`}>
                          {translations.programs[program].description[isTamil ? 'ta' : 'en']}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgramsPage;